import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/motion";
import { CONCEPTS } from "@/content/site";

const ParallaxImg = ({ src, alt, className, speed = 14 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed}%`, `${speed}%`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} loading="lazy" className="absolute inset-x-0 -top-[15%] h-[130%] w-full object-cover" />
    </div>
  );
};

export default function Story() {
  return (
    <>
      <Seo title="Our Story — TeaDM Bubble Tea, Northern Virginia" description="Tea DM began with a simple goal: a warm, inviting space for high-quality beverages that bring people together. Meet the brand behind Boba and the Beat." path="/our-story" />
      <PageHero testId="story-hero" bg="#8b6cf2" eyebrow="Our story" lines={["Made to", "bring people", "together."]} />

      <section className="bg-[#fff5e8] py-24 sm:py-32">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif-x text-3xl leading-snug sm:text-5xl">Tea DM began with a simple goal: to create a space where everyone can enjoy high-quality beverages in a warm and inviting atmosphere.</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 max-w-xl text-lg text-[#1a0a10]/85">We believe in crafting drinks that not only taste amazing but also bring people together. From our first location to now serving communities across the city, our dedication to freshness, flavor, and fun remains at the heart of everything we do.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <ParallaxImg src="/img/lineup-store.webp" alt="TeaDM drinks lined up on the counter" className="aspect-[3/4] rounded-[2.5rem]" />
          </div>
        </div>
      </section>

      <section className="bg-[#2e9e4f] py-24 text-[#fff5e8] sm:py-32">
        <div className="wrap grid items-center gap-12 lg:grid-cols-12">
          <ParallaxImg src="/img/mood-matcha-pour.webp" alt="Matcha being poured over milk and ice" className="aspect-[4/5] rounded-t-[999px] rounded-b-[2.5rem] lg:col-span-5" />
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="eyebrow">What makes us special</p>
            <MaskLines lines={["The perfect cup", "for every mood."]} className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg">At Tea DM, we're passionate about crafting the perfect cup for every mood. Whether you're craving a classic milk tea, a bold coffee blend, or a fruity smoothie, we have something special waiting for you.</p>
              <Link to="/menu" data-testid="story-menu-cta" className="btn btn-cream mt-10">Explore the menu <ArrowRight className="h-4 w-4" /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#1a0a10] py-24 text-[#fff5e8] sm:py-32">
        <div className="wrap">
          <p className="eyebrow text-[#ffd23f]">The TeaDM family</p>
          <MaskLines lines={["Our concepts."]} className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
          <div className="mt-14 grid gap-4 md:grid-cols-2">
            {CONCEPTS.map((c, i) => {
              const inner = (
                <>
                  <span className="font-display text-sm font-bold text-[#ffd23f]">0{i + 1}</span>
                  <h3 className="mt-6 font-display text-3xl font-extrabold">{c.name}</h3>
                  <p className="mt-3 text-[#fff5e8]/80">{c.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider">Visit <ArrowUpRight className="h-4 w-4" /></span>
                </>
              );
              const cls = "block rounded-[2rem] border border-white/10 p-8 transition-colors duration-500 hover:bg-white/5";
              return (
                <Reveal key={c.name} delay={i * 0.08}>
                  {c.to ? <Link to={c.to} className={cls} data-testid={`concept-${i}`}>{inner}</Link> : <a href={c.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`concept-${i}`}>{inner}</a>}
                </Reveal>
              );
            })}
          </div>
          <Link to="/careers" data-testid="story-careers-cta" className="btn btn-berry mt-14">Join our team <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </>
  );
}
